import React, { useMemo, useState } from 'react';
// UI
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// Context
import { useAuth } from 'AuthContext';
// Components
import THEME_COLORS from 'components/ClubBooksTab/themeColors';
import QuarterGoalsReportActivity from 'components/QuarterGoalsReportActivity';
import LoadingScreen from 'components/LoadingScreen';
// Services
import {
  requestPermissionAndSubscribe,
  sendTestNotification,
} from 'services/notifications/notifications.service';
// Utils
import triggerHaptic, { getHapticsSupport } from 'utils/haptics';
import { isNativeApp } from 'utils/platformHelpers';
import { getQuarterGoalsReportSamplePayload } from 'utils/quarterGoalsReportSamplePayload';

const DevTools = () => {
  const { user } = useAuth();
  const [toastState, setToastState] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [hapticsResult, setHapticsResult] = useState(null);
  const [testNotificationSending, setTestNotificationSending] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const quarterReportSample = useMemo(() => getQuarterGoalsReportSamplePayload(), []);

  const navigate = useNavigate();
  const muiTheme = useTheme();
  const themeColorEntries = useMemo(() => Object.entries(THEME_COLORS), []);
  const paletteEntries = useMemo(
    () => [
      { label: 'Primary', value: muiTheme?.palette?.primary?.main },
      { label: 'Secondary', value: muiTheme?.palette?.secondary?.main },
      { label: 'Accent', value: muiTheme?.palette?.accent?.main },
      { label: 'Background', value: muiTheme?.palette?.background?.default },
    ],
    [muiTheme]
  );

  const openToast = (severity, message) => {
    setToastState({
      open: true,
      severity,
      message,
    });
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setToastState((prev) => ({ ...prev, open: false }));
  };

  const handleHaptic = (type) => {
    // eslint-disable-next-line no-console
    console.log(`[dev] haptics: ${type}`);
    const result = triggerHaptic(type);
    setHapticsResult(result);
  };

  const handleRequestNotificationPermission = async () => {
    // eslint-disable-next-line no-console
    console.log('[dev] notifications: request permission');

    if (isNativeApp()) {
      if (!user?.uid) {
        openToast('warning', 'You must be signed in to register for push.');
        return;
      }
      const result = await requestPermissionAndSubscribe(user.uid);
      // eslint-disable-next-line no-console
      console.log('[dev] notifications: native subscribe result', result);
      if (result.success) {
        openToast('success', 'Push permission granted and device registered.');
        return;
      }
      openToast('warning', result.error || 'Failed to register for push.');
      return;
    }

    if (!('Notification' in window)) {
      // eslint-disable-next-line no-console
      console.log('[dev] notifications: unsupported');
      openToast('warning', 'Notifications are not supported in this browser.');
      return;
    }

    const result = await Notification.requestPermission();
    // eslint-disable-next-line no-console
    console.log('[dev] notifications: permission result', result);
    if (result === 'granted') {
      openToast('success', 'Notification permission granted.');
      return;
    }

    if (result === 'denied') {
      openToast('warning', 'Notification permission denied.');
      return;
    }

    openToast('info', 'Notification permission dismissed.');
  };

  const handleTestNotification = async () => {
    // eslint-disable-next-line no-console
    console.log('[dev] notifications: send test (local)');

    if (isNativeApp()) {
      if (!user?.uid) {
        openToast('warning', 'You must be signed in to send a test push.');
        return;
      }
      setTestNotificationSending(true);
      try {
        await sendTestNotification(
          user.uid,
          'CBC Test Notification',
          'This is a test push from /dev.'
        );
        openToast('success', 'Test push sent (check your device).');
      } catch (err) {
        openToast('error', err.message || 'Failed to send test push.');
      } finally {
        setTestNotificationSending(false);
      }
      return;
    }

    if (!('Notification' in window)) {
      openToast('warning', 'Notifications are not supported in this browser.');
      return;
    }
    if (Notification.permission !== 'granted') {
      openToast('info', 'Grant permission before sending a test notification.');
      return;
    }
    try {
      new Notification('CBC Test Notification', {
        body: 'This is a test notification from /dev.',
      });
      openToast('success', 'Notification sent (check your system).');
    } catch (error) {
      openToast('warning', 'Failed to send test notification.');
    }
  };

  const handleTestPushNotification = async (type) => {
    if (!user?.uid) {
      openToast('warning', 'You must be signed in to send a test push.');
      return;
    }
    setTestNotificationSending(true);
    try {
      await sendTestNotification(user.uid, undefined, undefined, type);
      openToast('success', `Test ${type || 'default'} push notification sent.`);
    } catch (err) {
      openToast('error', err.message || 'Failed to send test push notification.');
    } finally {
      setTestNotificationSending(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Back to Dashboard
        </Button>
      </Box>
      <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
        Dev Tools
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Quick sanity checks for toasts, theme colors, haptics, and notifications.
      </Typography>

      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Loading Screen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Preview the app startup loader. Tap anywhere on it to exit.
          </Typography>
          <Button variant="contained" onClick={() => setShowLoader(true)}>
            Show loader
          </Button>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Toasts
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" color="success" onClick={() => openToast('success', 'Success toast test')}>
              Success
            </Button>
            <Button variant="contained" color="warning" onClick={() => openToast('warning', 'Warning toast test')}>
              Warning
            </Button>
            <Button variant="contained" color="error" onClick={() => openToast('error', 'Error toast test')}>
              Error
            </Button>
            <Button variant="contained" color="info" onClick={() => openToast('info', 'Info toast test')}>
              Info
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Theme Colors
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Palette
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {paletteEntries.map((entry) => {
                  const colorValue = entry.value || '#FFFFFF';
                  return (
                    <Box key={entry.label} sx={{ flex: 1 }}>
                      <Box sx={{ height: 48, borderRadius: 1, bgcolor: colorValue, mb: 1 }} />
                      <Typography variant="body2">
                        {entry.label}: {entry.value || 'N/A'}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Meeting Themes
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {themeColorEntries.map(([label, value]) => (
                  <Box key={label} sx={{ flex: 1 }}>
                    <Box sx={{ height: 48, borderRadius: 1, bgcolor: value, mb: 1 }} />
                    <Typography variant="body2">
                      {label}: {value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Haptics
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="outlined" onClick={() => handleHaptic('light')}>
              Light
            </Button>
            <Button variant="outlined" onClick={() => handleHaptic('medium')}>
              Medium
            </Button>
            <Button variant="outlined" onClick={() => handleHaptic('heavy')}>
              Heavy
            </Button>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            {(() => {
              const support = getHapticsSupport();
              return (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Secure context: {support.isSecureContext ? 'yes' : 'no'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vibration API: {support.hasVibrate ? 'available' : 'missing'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    User activation: {support.hasUserActivation ? 'yes' : 'no'}
                  </Typography>
                </>
              );
            })()}
            {hapticsResult && (
              <Typography variant="body2" color="text.secondary">
                Last result: {hapticsResult.didVibrate ? 'vibrated' : 'no vibration'}
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Notifications
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <Button variant="contained" onClick={handleRequestNotificationPermission}>
              Request Permission
            </Button>
            <Button
              variant="outlined"
              onClick={handleTestNotification}
              disabled={testNotificationSending}
            >
              Test (local)
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleTestPushNotification('feed')}
              disabled={testNotificationSending}
            >
              Test feed
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleTestPushNotification('goal')}
              disabled={testNotificationSending}
            >
              Test goal
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleTestPushNotification('meeting')}
              disabled={testNotificationSending}
            >
              Test meeting
            </Button>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Quarter goals report (feed preview)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sample preview with placeholder data (no API required).
          </Typography>

          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'action.selected',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.contrastText',
                    fontSize: '0.6875rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Activity
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Sample feed post
              </Typography>
            </Box>
            <QuarterGoalsReportActivity payload={quarterReportSample} />
          </Box>

          <Button variant="outlined" onClick={() => navigate('/feed')}>
            Open feed
          </Button>
        </Paper>
      </Stack>

      {showLoader && (
        <Box
          onClick={() => setShowLoader(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            cursor: 'pointer',
          }}
        >
          <LoadingScreen />
        </Box>
      )}

      <Snackbar
        open={toastState.open}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toastState.severity} sx={{ width: '100%' }}>
          {toastState.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DevTools;
